import { differenceInCalendarDays } from 'date-fns'
import { type ReactNode } from 'react'
import { gemVar, getResourceGlyph, ThemedColors } from '@opallabs/gem'
import { useTheme } from '@opallabs/hooks'
import { Icon } from '@opallabs/lapidary'
import { parseCalendarDate } from '../monthGrid'
import { barColors } from '../resolveItemColor'
import { CalendarSizes } from '../sizes'
import { type CalendarItem } from '../types'
import * as styles from './styles'

/**
 * How many days the item covers, counted the way a person would: an item that
 * starts and ends on the same day covers one day. Null when a date cannot be
 * read.
 */
const spanDays = (item: CalendarItem): number | null => {
  // Content runs from the day it is scheduled until it is taken down, and that
  // window is the width the grid drew. Its own dates are the launch day alone.
  const range = item.inMarket ?? item
  const start = parseCalendarDate(range.start, { field: 'start', id: item.id })
  const end = parseCalendarDate(range.end, { field: 'end', id: item.id })
  if (start === null || end === null) return null
  return differenceInCalendarDays(end, start) + 1
}

export type DragGhostProps = Readonly<{
  /** The color the item is drawn in on the grid, when it has one. */
  color?: string
  item: CalendarItem
  renderChannelIcon?: (item: CalendarItem) => ReactNode
}>

/**
 * What a person sees under the pointer while they drag.
 *
 * Every item condenses to this one line, whatever the grid drew: a chip, a card
 * two lines tall, and a moment's bar reaching across a week all leave the grid
 * as the same shape. Dragging a week-wide bar around by a week-wide picture of
 * it hides the days it is being dropped on, which is the one thing the person
 * is looking at.
 *
 * Anything that runs for more than a day says how long it runs, because the
 * width that makes it draggable is the width that took its length away.
 *
 * Figma: "Month - states", the Drag A row (4030:24338) — the content chip
 * 4033:12777 and the moment row 4034:12811.
 */
export const DragGhost: React.FC<DragGhostProps> = ({
  color,
  item,
  renderChannelIcon,
}) => {
  const theme = useTheme()
  const derived = color ? barColors(color) : null
  const isMoment = item.kind !== 'content'
  const days = spanDays(item)

  const leadingMark = isMoment ? (
    <Icon
      ariaHidden={true}
      glyph={getResourceGlyph(theme, item.kind, 'outlined')}
      size={CalendarSizes.barIconSize}
    />
  ) : (
    renderChannelIcon?.(item)
  )

  return (
    <span
      css={[styles.ghost, !isMoment && styles.contentGhost]}
      // A moment keeps its bar's tint. A moment with no color of its own would
      // otherwise be a ghost with no face at all.
      style={
        isMoment
          ? { backgroundColor: derived?.fill ?? gemVar(ThemedColors.Surface2) }
          : undefined
      }
    >
      {derived && (
        <span css={styles.swatch} style={{ backgroundColor: derived.border }} />
      )}
      {leadingMark && <span css={styles.mark}>{leadingMark}</span>}
      <span css={styles.name}>
        {!isMoment && item.time !== undefined && (
          <span css={styles.time}>{item.time}</span>
        )}
        {days !== null && days > 1 ? `${item.title} · ${days}d` : item.title}
      </span>
      {isMoment && !!item.childCount && (
        <span css={styles.count}>{item.childCount}</span>
      )}
    </span>
  )
}
DragGhost.displayName = 'Calendar: DragGhost'
